import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ShieldAlert, RefreshCw } from 'lucide-react';
import { useLocation } from 'react-router-dom';

// Pages where ad blocker should be enforced (content pages)
const BLOCKED_PATHS = ['/browse', '/film/', '/watch/', '/browse/films', '/browse/series'];

const detectAdBlocker = () => {
  return new Promise((resolve) => {
    // Create bait elements with class names that ad blockers target via CSS rules
    const bait = document.createElement('div');
    bait.className = 'ad_unit ad-zone textAd advertisment adsbox';
    bait.style.cssText = 'width:1px;height:1px;position:fixed;top:0;left:0;background:transparent;pointer-events:none;z-index:-1;';
    document.body.appendChild(bait);

    const bait2 = document.createElement('div');
    bait2.className = 'adsbygoogle';
    bait2.style.cssText = 'width:1px;height:1px;position:fixed;top:0;left:0;background:transparent;pointer-events:none;z-index:-1;display:block;';
    document.body.appendChild(bait2);

    // Also try fetching a common ad resource
    let fetchBlocked = false;
    const img = new Image();
    img.onerror = () => { fetchBlocked = true; };
    img.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?t=' + Date.now();

    setTimeout(() => {
      const style1 = getComputedStyle(bait);
      const style2 = getComputedStyle(bait2);

      const hidden1 = style1.display === 'none' || style1.visibility === 'hidden' || bait.offsetHeight === 0;
      const hidden2 = style2.display === 'none' || style2.visibility === 'hidden' || bait2.offsetHeight === 0;

      bait.remove();
      bait2.remove();

      // Trigger only if CSS-based hiding detected (most reliable for real ad blockers)
      resolve(hidden1 || hidden2);
    }, 250);
  });
};

const AdBlockDetector = () => {
  const [adBlockDetected, setAdBlockDetected] = useState(false);
  const [checking, setChecking] = useState(false);
  const location = useLocation();

  const isContentPage = BLOCKED_PATHS.some(p => location.pathname.startsWith(p));

  // Continuous monitoring on content pages
  useEffect(() => {
    if (!isContentPage) {
      setAdBlockDetected(false);
      return;
    }
    // Initial check
    detectAdBlocker().then(setAdBlockDetected);

    // Periodic re-check (catches enable/disable mid-session)
    const interval = setInterval(async () => {
      const detected = await detectAdBlocker();
      setAdBlockDetected(detected);
    }, 10000);
    return () => clearInterval(interval);
  }, [isContentPage]);

  const handleRecheck = async () => {
    setChecking(true);
    await new Promise(r => setTimeout(r, 500));
    const detected = await detectAdBlocker();
    setAdBlockDetected(detected);
    setChecking(false);
  };

  if (!adBlockDetected || !isContentPage) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] bg-[#030014]/95 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="adblock-overlay">
      <div className="bg-[#0F0B1E] border border-[#8B5CF6]/30 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl shadow-[#8B5CF6]/10">
        {/* Header accent */}
        <div className="h-1 bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#8B5CF6]" />

        <div className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#8B5CF6]/15 flex items-center justify-center mx-auto mb-5">
            <ShieldAlert className="w-8 h-8 text-[#A78BFA]" />
          </div>

          <h2 className="text-xl font-bold text-white mb-3"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="adblock-title">
            Ad Blocker Detected
          </h2>

          <p className="text-gray-400 text-sm leading-relaxed mb-6">
            To watch movies and series on <span className="text-white font-medium">reframes</span>,
            please disable your ad blocker. Ads help us support creators and keep the platform running.
          </p>

          <div className="bg-white/5 rounded-xl p-4 mb-6 text-left space-y-2">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">How to disable:</p>
            <p className="text-sm text-gray-300">1. Click the ad blocker icon in your browser toolbar</p>
            <p className="text-sm text-gray-300">2. Select "Pause" or "Disable for this site"</p>
            <p className="text-sm text-gray-300">3. Click the button below to continue</p>
          </div>

          <button
            onClick={handleRecheck}
            disabled={checking}
            className="w-full py-3 bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-xl text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="adblock-recheck-btn"
          >
            {checking ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Checking...</>
            ) : (
              <><RefreshCw className="w-4 h-4" /> I've Disabled It — Continue</>
            )}
          </button>

          <p className="text-xs text-gray-600 mt-4">
            The page will also unlock automatically when the ad blocker is disabled.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdBlockDetector;
